import { CSSProperties } from 'react';
import { tokens } from '../SapphireProvider';
import { type BreakpointKey } from './useBreakpointTokens';

export type BaseResponsiveProp<T> =
  /**
   * Support design-token breakpoint values for both media queries and container queries.
   */
  { [key in BreakpointKey]?: T } & {
    /**
     * Used when the resolved width is less than all breakpoints. Or if breakpoint
     * detection is not possible for any reason (like being run in SSR),
     */
    base: T;
  };

export type ViewportResponsiveProp<T> = BaseResponsiveProp<T> & {
  container?: never;
  [key: number]: never;
};

export type ContainerResponsiveProp<T> = BaseResponsiveProp<T> & {
  /**
   * Provide a container name to target a specific container.
   * If set to `true`, the element will target the closest container.
   *
   * When a container name is provided, the string is used as a container query selector, as per
   * the documentation:
   * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_queries#naming_containment_contexts.
   * This functionality requires that a layout element has `containerName` property set to the same
   * value.
   */
  container: true | string;
  /**
   * Container queries support passing a number value as a breakpoint, since some components may
   * not take up the space required to hit the design-tokens breakpoints.
   */
  [key: number]: T;
};

export type ResponsiveProp<T> =
  | ViewportResponsiveProp<T>
  | ContainerResponsiveProp<T>;

export type Responsive<T> = ResponsiveProp<T> | T;

/**
 * Represents either a single value or a set of different values for each breakpoint.
 * In both cases, the type of the value itself comes from a CSS property specified
 * by K and also a design token (a value from `theme`) of type T, if provided.
 * By default, it doesn't allow any theme value.
 */
export type ResponsiveCssProp<
  K extends keyof CSSProperties,
  T extends keyof typeof tokens | '' = ''
> = Responsive<
  | CSSProperties[K]
  | (T extends keyof typeof tokens ? ValueOf<(typeof tokens)[T]> : never)
>;

type ValueOf<T> = T[keyof T];

export interface SapphireCommonStyleProps {
  // Spacing
  margin?: ResponsiveCssProp<'margin', 'size'>;
  marginLeft?: ResponsiveCssProp<'marginLeft', 'size'>;
  marginRight?: ResponsiveCssProp<'marginRight', 'size'>;
  marginTop?: ResponsiveCssProp<'marginTop', 'size'>;
  marginBottom?: ResponsiveCssProp<'marginBottom', 'size'>;
  /**
   * Shorthand for marginLeft and marginRight
   */
  marginX?: ResponsiveCssProp<'marginBottom', 'size'>;
  /**
   * Shorthand for marginTop and marginBottom
   */
  marginY?: ResponsiveCssProp<'marginBottom', 'size'>;
  width?: ResponsiveCssProp<'width', 'size'>;
  height?: ResponsiveCssProp<'height', 'size'>;
  minWidth?: ResponsiveCssProp<'minWidth', 'size'>;
  minHeight?: ResponsiveCssProp<'minHeight', 'size'>;
  maxWidth?: ResponsiveCssProp<'maxWidth', 'size'>;
  maxHeight?: ResponsiveCssProp<'maxHeight', 'size'>;

  // Layout
  flex?: ResponsiveCssProp<'flex'>;
  flexGrow?: ResponsiveCssProp<'flexGrow'>;
  flexShrink?: ResponsiveCssProp<'flexShrink'>;
  flexBasis?: ResponsiveCssProp<'flexBasis'>;
  justifySelf?: ResponsiveCssProp<'justifySelf'>;
  alignSelf?: ResponsiveCssProp<'alignSelf'>;
  order?: ResponsiveCssProp<'order'>;
  gridArea?: ResponsiveCssProp<'gridArea'>;
  gridColumn?: ResponsiveCssProp<'gridColumn'>;
  gridRow?: ResponsiveCssProp<'gridRow'>;
  gridColumnStart?: ResponsiveCssProp<'gridColumnStart'>;
  gridColumnEnd?: ResponsiveCssProp<'gridColumnEnd'>;
  gridRowStart?: ResponsiveCssProp<'gridRowStart'>;
  gridRowEnd?: ResponsiveCssProp<'gridRowEnd'>;

  // Positioning
  position?: ResponsiveCssProp<'position'>;
  zIndex?: ResponsiveCssProp<'zIndex'>;
  top?: ResponsiveCssProp<'top', 'size'>;
  bottom?: ResponsiveCssProp<'bottom', 'size'>;
  left?: ResponsiveCssProp<'left', 'size'>;
  right?: ResponsiveCssProp<'right', 'size'>;
  inset?: ResponsiveCssProp<'marginBottom', 'size'>;

  isHidden?: Responsive<boolean>;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type StyleHandler<T extends keyof CSSProperties> =
  | keyof CSSProperties
  | {
      styleProp: T;
      convertValue: (value: any) => CSSProperties[T] | undefined;
    }
  | {
      styleProps: T[];
    };

export type StyleHandlers<
  T extends {
    [key: string]: ResponsiveCssProp<any, any> | Responsive<any>;
  } = SapphireCommonStyleProps
> = {
  [key in keyof T]-?: StyleHandler<any>;
};

const isHiddenHandler: StyleHandler<'display'> = {
  styleProp: 'display',
  convertValue: (value: boolean) => (value ? 'none' : undefined),
};

export const commonStyleHandlers: StyleHandlers = {
  margin: 'margin',
  marginLeft: 'marginLeft',
  marginRight: 'marginRight',
  marginTop: 'marginTop',
  marginBottom: 'marginBottom',
  marginX: {
    styleProps: ['marginLeft', 'marginRight'] as Array<keyof CSSProperties>,
  },
  marginY: {
    styleProps: ['marginTop', 'marginBottom'] as Array<keyof CSSProperties>,
  },
  width: 'width',
  height: 'height',
  minWidth: 'minWidth',
  minHeight: 'minHeight',
  maxWidth: 'maxWidth',
  maxHeight: 'maxHeight',
  flex: 'flex',
  flexGrow: 'flexGrow',
  flexShrink: 'flexShrink',
  flexBasis: 'flexBasis',
  justifySelf: 'justifySelf',
  alignSelf: 'alignSelf',
  order: 'order',
  gridArea: 'gridArea',
  gridColumn: 'gridColumn',
  gridColumnEnd: 'gridColumnEnd',
  gridColumnStart: 'gridColumnStart',
  gridRow: 'gridRow',
  gridRowEnd: 'gridRowEnd',
  gridRowStart: 'gridRowStart',
  position: 'position',
  zIndex: 'zIndex',
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
  inset: 'inset',

  isHidden: isHiddenHandler,
};
